/**
 * Operator-Implementierungen fuer die Check-Regeln — EINE Quelle fuer beide Seiten.
 *
 * WARUM HIER UND NICHT IM SERVER
 *   Bis 08/2026 gab es zwei Implementierungen: eine im ruleEngine (echter Scan) und
 *   eine als `evaluateCondition` in der CheckLibraryPage (Simulation). Sie sind
 *   auseinandergelaufen — der Simulation fehlten `in`, `regex`, `includes_only`,
 *   `has_key`, `array_length_gte` und `array_length_eq`. Regeln mit diesen Operatoren
 *   meldeten in der Vorschau still "kein Match", waehrend der echte Scan matchte.
 *   Deshalb liegt die Logik jetzt hier und wird von beiden Seiten importiert.
 *
 * KEINE Server-Abhaengigkeiten in dieser Datei (kein db(), kein sequelize) — sie wird
 * mit in den Browser gebuendelt.
 */

export type OperatorFn = (fieldValue: unknown, expected: unknown) => boolean;

function toNumber(val: unknown): number | null {
  if (typeof val === 'number') return val;
  if (typeof val === 'string') {
    const n = Number(val);
    return isNaN(n) ? null : n;
  }
  return null;
}

function toArray(val: unknown): unknown[] | null {
  if (Array.isArray(val)) return val;
  return null;
}

export function toComparableString(val: unknown): string {
  if (val === null || val === undefined) return '';
  if (typeof val === 'string') return val;
  // Objects/arrays: use JSON.stringify so operators behave like the frontend evaluateCondition
  if (typeof val === 'object') return JSON.stringify(val);
  return String(val);
}

export const operators: Record<string, OperatorFn> = {
  equals(fieldValue, expected) {
    if (typeof expected === 'string') {
      try {
        const parsed = JSON.parse(expected);
        // Only strict-type compare when fieldValue itself is not a string.
        // If fieldValue IS a string (e.g. "true"), fall through to case-insensitive string compare
        // so "true" equals "true" consistently with the client simulation.
        if ((typeof parsed === 'boolean' || typeof parsed === 'number') && typeof fieldValue !== 'string') {
          return fieldValue === parsed;
        }
      } catch { /* use raw string */ }
    }
    // Case-insensitive string comparison — mirrors frontend evaluateCondition
    return toComparableString(fieldValue).toLowerCase() === String(expected).toLowerCase();
  },

  not_equals(fieldValue, expected) {
    return !operators.equals(fieldValue, expected);
  },

  contains(fieldValue, expected) {
    const expStr = (typeof expected === 'string' ? expected : String(expected)).toLowerCase();
    if (typeof fieldValue === 'string') return fieldValue.toLowerCase().includes(expStr);
    if (Array.isArray(fieldValue)) {
      // Exact case-insensitive match per array element — mirrors client evaluateCondition
      return fieldValue.some(item => String(item).toLowerCase() === expStr);
    }
    // Objects: serialize to JSON so key/value strings are searchable
    return toComparableString(fieldValue).toLowerCase().includes(expStr);
  },

  not_contains(fieldValue, expected) {
    return !operators.contains(fieldValue, expected);
  },

  gt(fieldValue, expected) {
    const a = toNumber(fieldValue);
    const b = toNumber(expected);
    if (a === null || b === null) return false;
    return a > b;
  },

  gte(fieldValue, expected) {
    const a = toNumber(fieldValue);
    const b = toNumber(expected);
    if (a === null || b === null) return false;
    return a >= b;
  },

  lt(fieldValue, expected) {
    const a = toNumber(fieldValue);
    const b = toNumber(expected);
    if (a === null || b === null) return false;
    return a < b;
  },

  lte(fieldValue, expected) {
    const a = toNumber(fieldValue);
    const b = toNumber(expected);
    if (a === null || b === null) return false;
    return a <= b;
  },

  exists(fieldValue, _expected) {
    return fieldValue !== null && fieldValue !== undefined;
  },

  not_exists(fieldValue, _expected) {
    return fieldValue === null || fieldValue === undefined;
  },

  in(fieldValue, expected) {
    let list: unknown[];
    if (typeof expected === 'string') {
      try { list = JSON.parse(expected); } catch { list = expected.split(',').map(s => s.trim()); }
    } else if (Array.isArray(expected)) {
      list = expected;
    } else {
      return false;
    }
    // Case-insensitive comparison for strings
    const fieldStr = String(fieldValue).toLowerCase();
    return list.some(item => String(item).toLowerCase() === fieldStr);
  },

  regex(fieldValue, expected) {
    if (typeof fieldValue !== 'string' || typeof expected !== 'string') return false;
    try {
      return new RegExp(expected, 'i').test(fieldValue);
    } catch {
      return false;
    }
  },

  array_length_gte(fieldValue, expected) {
    const arr = toArray(fieldValue);
    const n = toNumber(expected);
    if (!arr || n === null) return false;
    return arr.length >= n;
  },

  array_length_eq(fieldValue, expected) {
    const arr = toArray(fieldValue);
    const n = toNumber(expected);
    if (!arr || n === null) return false;
    return arr.length === n;
  },

  includes_only(fieldValue, expected) {
    const arr = toArray(fieldValue);
    if (!arr) return false;
    let allowed: string[];
    if (typeof expected === 'string') {
      try { allowed = JSON.parse(expected); } catch { allowed = expected.split(',').map(s => s.trim()); }
    } else if (Array.isArray(expected)) {
      allowed = expected.map(String);
    } else {
      return false;
    }
    const allowedSet = new Set(allowed.map(s => s.toLowerCase()));
    const items = arr.map(item => typeof item === 'string' ? item.toLowerCase() : String(item).toLowerCase());
    return items.length === 0 || items.every(item => allowedSet.has(item));
  },

  // Alias: UI saves operator_key as 'array_contains_only'; same semantics as includes_only
  array_contains_only(fieldValue, expected) {
    return operators.includes_only(fieldValue, expected);
  },

  array_contains_all(fieldValue, expected) {
    const arr = toArray(fieldValue);
    if (!arr) return false;
    const required = (typeof expected === 'string' ? expected : String(expected))
      .split(',').map(s => s.trim().toLowerCase());
    return required.every(req => arr.some(item => String(item).toLowerCase() === req));
  },

  array_max_items(fieldValue, expected) {
    const arr = toArray(fieldValue);
    if (!arr) return false;
    const n = toNumber(expected);
    if (n === null) return false;
    return arr.length <= n;
  },

  array_has_extra(fieldValue, expected) {
    const arr = toArray(fieldValue);
    if (!arr) return false;
    const allowed = (typeof expected === 'string' ? expected : String(expected))
      .split(',').map(s => s.trim().toLowerCase());
    return arr.some(item => !allowed.includes(String(item).toLowerCase()));
  },

  has_key(fieldValue, expected) {
    if (!fieldValue || typeof fieldValue !== 'object' || Array.isArray(fieldValue)) return false;
    const key = typeof expected === 'string' ? expected.replace(/^"|"$/g, '') : String(expected);
    return key in (fieldValue as Record<string, unknown>);
  },

  array_is_empty(fieldValue, _expected) {
    if (fieldValue === null || fieldValue === undefined) return true;
    return Array.isArray(fieldValue) && fieldValue.length === 0;
  },

  array_not_empty(fieldValue, _expected) {
    return Array.isArray(fieldValue) && fieldValue.length > 0;
  },

  array_has_key_value(fieldValue, expected) {
    const expStr = typeof expected === 'string' ? expected : String(expected);
    const eqIdx = expStr.indexOf('=');
    if (eqIdx === -1) return false;
    const key = expStr.slice(0, eqIdx);
    const val = expStr.slice(eqIdx + 1).toLowerCase();
    // Plain object: check key directly
    if (fieldValue && typeof fieldValue === 'object' && !Array.isArray(fieldValue)) {
      const itemVal = (fieldValue as Record<string, unknown>)[key];
      if (itemVal === undefined || itemVal === null) return false;
      return String(itemVal).toLowerCase() === val;
    }
    // Array: check if any item has the key-value pair
    if (!Array.isArray(fieldValue)) return false;
    return (fieldValue as unknown[]).some((item) => {
      if (!item || typeof item !== 'object' || Array.isArray(item)) return false;
      const itemVal = (item as Record<string, unknown>)[key];
      if (itemVal === undefined || itemVal === null) return false;
      return String(itemVal).toLowerCase() === val;
    });
  },

  in_alias_group(_fieldValue, _expected) {
    // Phase 2: requires alias lookup against DB
    return false;
  },

  is_true(fieldValue, _expected) {
    return fieldValue === true || fieldValue === 'true';
  },

  is_false(fieldValue, _expected) {
    return fieldValue === false || fieldValue === 'false';
  },
};

/** Bekannter Operator? Trennt "trifft nicht zu" von "kennt den Operator nicht". */
export function isKnownOperator(key: string): boolean {
  return Object.prototype.hasOwnProperty.call(operators, key);
}

/**
 * Wertet eine einzelne Bedingung aus. `null` heisst: Operator unbekannt — das ist
 * etwas anderes als `false` und muss dem Nutzer auch anders gezeigt werden.
 */
export function applyOperator(operatorKey: string, fieldValue: unknown, expected: unknown): boolean | null {
  const fn = operators[operatorKey];
  if (!fn) return null;
  return fn(fieldValue, expected);
}
